<template>
  <div class="posts-frame">
    <div class="posts-scroll">
      <table class="posts-table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :style="col.width ? { width: col.width } : null"
            >
              <span class="th-info">
                {{ col.label }}
                <fluent-icon
                  v-if="col.tooltip"
                  v-tooltip.top="col.tooltip"
                  class="info-icon"
                  icon="info"
                  size="14"
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row[rowKey]">
            <td
              v-for="col in columns"
              :key="col.key"
              :class="{ num: col.type === 'metric', date: col.type === 'date' }"
            >
              <div v-if="col.type === 'user'" class="poster">
                <span class="avatar">{{ initials(cellValue(col, row)) }}</span>
                <span class="poster-name">{{ cellValue(col, row) }}</span>
              </div>

              <span
                v-else-if="col.type === 'status'"
                class="status-tag"
                :class="`status-${cellValue(col, row).variant}`"
              >
                {{ cellValue(col, row).label }}
              </span>

              <div v-else-if="col.type === 'content'" class="content-cell">
                <span class="content-text" :title="cellValue(col, row)">
                  {{ cellValue(col, row) }}
                </span>
                <button
                  v-tooltip.top="labels.copy"
                  class="ghost-btn ghost-btn--sm"
                  type="button"
                  @click="$emit('copy', cellValue(col, row))"
                >
                  <fluent-icon icon="clipboard" size="16" />
                </button>
              </div>

              <template v-else-if="col.type === 'date'">
                {{ formatDate(cellValue(col, row)) }}
              </template>

              <div v-else-if="col.type === 'actions'" class="row-actions">
                <button
                  v-tooltip.top="labels.edit"
                  class="ghost-btn"
                  type="button"
                  @click="$emit('edit', row)"
                >
                  <fluent-icon icon="edit" size="20" />
                </button>
                <button
                  v-tooltip.top="labels.delete"
                  class="ghost-btn ghost-btn--danger"
                  type="button"
                  @click="$emit('delete', row)"
                >
                  <fluent-icon icon="delete" size="20" />
                </button>
              </div>

              <template v-else>{{ cellValue(col, row) }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    // [{ key, label, type?: 'text'|'user'|'status'|'content'|'metric'|'date'|'actions',
    //    width?, tooltip?, value?: (row) => any }]
    // `value` overrides the default `row[key]` lookup; for `status` it must
    // return { label, variant: 'completed'|'failed'|'pending' }.
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    rowKey: {
      type: String,
      default: 'id',
    },
    // Tooltip labels for the copy / edit / delete buttons.
    labels: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    cellValue(col, row) {
      return col.value ? col.value(row) : row[col.key];
    },
    initials(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return '?';
      const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (parts[0][0] + last).toUpperCase();
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.posts-frame,
.posts-frame *,
.posts-frame *::before,
.posts-frame *::after {
  box-sizing: border-box;
}

/* Rounded, outlined container with its own scroll. */
.posts-frame {
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  outline: 1px solid #f3f4f6;
  outline-offset: -1px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.posts-scroll {
  flex: 1;
  overflow: auto;
}

.posts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.posts-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #e5e5e5;
  color: #717171;
  font-weight: 600;
  line-height: 20px;
  text-transform: uppercase;
  text-align: start;
  padding: 8px 12px;
  white-space: nowrap;
}
.posts-table tbody td {
  height: 52px;
  padding: 12px;
  color: #717171;
  font-weight: 400;
  line-height: 24px;
  vertical-align: middle;
  background: #ffffff;
  transition: background 0.12s;
}
.posts-table tbody tr:hover td {
  background: #f9fafb;
}

.th-info {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.info-icon {
  color: #6b7280;
  cursor: help;
}

/* Poster */
.poster {
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}
.avatar {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.poster-name {
  color: #323431;
}

/* Status tag */
.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  outline: 1px solid;
  outline-offset: -1px;
}
.status-completed {
  background: rgba(31, 132, 90, 0.08);
  outline-color: #1f845a;
  color: #1f845a;
}
.status-failed {
  background: rgba(239, 68, 68, 0.08);
  outline-color: #ef4444;
  color: #ef4444;
}
.status-pending {
  background: rgba(217, 119, 6, 0.08);
  outline-color: #d97706;
  color: #d97706;
}

/* Content */
.content-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 320px;
}
.content-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.num {
  text-align: center;
}
.date {
  white-space: nowrap;
}

/* Ghost icon buttons (edit / delete / copy). */
.row-actions {
  display: flex;
  gap: 5px;
}
.ghost-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  box-shadow: 0px 1px 8px rgba(221, 221, 221, 0.25);
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s;
}
.ghost-btn:hover {
  background: #f9fafb;
}
.ghost-btn--sm {
  width: 32px;
  height: 32px;
}
.ghost-btn--danger {
  color: #ef4444;
}
</style>
